// SupportDetailModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Table, Badge, Spinner
} from "reactstrap";

/**
 * แนวคิด API ฝั่งหลังบ้าน:
 * - แนะนำทำ endpoint เดียวแบบ dynamic:
 *   GET /api/Support/operation/detail/{supportTypeId}/{supportId}
 *   -> คืน JSON: { supportTypeId, supportId, title, data, children? }
 *   โดย data คือ object (คีย์ตาม ViewModel) และ children เป็นอาร์เรย์ของรายละเอียด (ถ้ามี)
 *
 * ตัวอย่าง client ด้านล่างจะเรียก endpoint นี้
 */

// ------------------ Field Config (ต่อประเภท) ------------------
const FIELD_CONFIG = {
  // supportTypeId == 1 -> ใช้ข้อมูลตาม Operation4
  1: [
    { key: "supportNo", label: "เลขที่คำขอ" },
    { key: "supportName", label: "ผู้ขอรับเงินช่วยเหลือ/อุดหนุน" },
    { key: "year", label: "ปี พ.ศ." },
    { key: "passTest", label: "จำนวนผู้ผ่านการทดสอบฯ" },
    { key: "lawOccupationName", label: "สาขาอาชีพ" },
    { key: "testOccupationName", label: "สาขา" },
    { key: "personTotal", label: "จำนวน (คน)" },
    { key: "paymentAmount", label: "จำนวนเงินช่วยเหลือ/อุดหนุน" },
    { key: "paymentDate", label: "วันที่จ่ายเงิน" },
  ],

  // Operation41
  2: [
    { key: "supportNo", label: "เลขที่คำขอ" },
    { key: "supportName", label: "ผู้ขอรับเงินช่วยเหลือ/อุดหนุน" },
    { key: "year2", label: "ปี พ.ศ. นำส่งเงินสมทบ" },
    { key: "receiptDate", label: "ว/ด/ป" },
    { key: "amount", label: "จำนวนเงิน" },
    { key: "year1", label: "ปี พ.ศ." },
    { key: "avgMan", label: "จำนวนลูกจ้างเฉลี่ย" },
    { key: "perc", label: "% การฝึก" },
    { key: "lawTrainMan", label: "ลูกจ้างตามกฎหมาย" },
    { key: "spktrainMan", label: "ลูกจ้างที่พัฒนา (สพร.)" },
    { key: "train", label: "อบรม" },
    { key: "test", label: "ทดสอบ" },
    { key: "oloc", label: "นอกสถานที่" },
    { key: "paymentAmount", label: "จำนวนเงินช่วยเหลือ/อุดหนุน" },
    { key: "paymentDate", label: "วันที่จ่ายเงิน" },
  ],

  // Operation42
  4: [
    { key: "supportNo", label: "เลขที่คำขอ" },
    { key: "supportName", label: "ผู้ขอรับเงินช่วยเหลือ/อุดหนุน" },
    { key: "receiveYear", label: "ปี พ.ศ. รับเงินสมทบ" },
    { key: "avgMan", label: "ลูกจ้างเฉลี่ย" },
    { key: "avg70", label: "เฉลี่ย 70%" },
    { key: "spkTrainMan", label: "ลูกจ้างพัฒนา (สพร.)" },
    { key: "trainPercent", label: "% การฝึก" },
    { key: "cntGt70Lt80", label: "จำนวนระหว่าง 70-80%" },
    { key: "cntGe80", label: "จำนวน ≥ 80%" },
    { key: "amt70", label: "วงเงิน 70%" },
    { key: "amt80", label: "วงเงิน 80%" },
    { key: "train", label: "อบรม" },
    { key: "test", label: "ทดสอบ" },
    { key: "oloc", label: "นอกสถานที่" },
    { key: "paymentAmount", label: "จำนวนเงินช่วยเหลือ/อุดหนุน" },
    { key: "paymentDate", label: "วันที่จ่ายเงิน" },
  ],

  // Operation43
  5: [
    { key: "supportNo", label: "เลขที่คำขอ" },
    { key: "supportName", label: "ผู้ขอรับเงินช่วยเหลือ/อุดหนุน" },
    { key: "skillStandardCertNoRmth3", label: "เลขที่ รมธ.3" },
    { key: "certificate26No", label: "เลขที่ใบรับรอง มาตรา 26" },
    { key: "lawOccupationName", label: "สาขาอาชีพ" },
    { key: "testOccupationName", label: "สาขา" },
    { key: "personTotal", label: "จำนวน (คน)" },
    { key: "certificateDate", label: "วันที่ออกใบรับรอง" },
    { key: "certificateTestDate", label: "วันที่ทดสอบ" },
    { key: "employeeTotal", label: "พนักงานทั้งหมด" },
    { key: "paymentAmount", label: "จำนวนเงินช่วยเหลือ/อุดหนุน" },
    { key: "paymentDate", label: "วันที่จ่ายเงิน" },
  ],

  // Operation44
  6: [
    { key: "supportNo", label: "เลขที่คำขอ" },
    { key: "supportName", label: "ผู้ขอรับเงินช่วยเหลือ/อุดหนุน" },
    { key: "certificateNo", label: "เลขที่ใบรับรอง" },
    { key: "certificateDate", label: "วันที่รับรอง" },
    { key: "lawOccupationName", label: "สาขาอาชีพ" },
    { key: "testOccupationName", label: "สาขา" },
    { key: "level", label: "ระดับ" },
    { key: "fullName", label: "ชื่อ-สกุลพนักงาน" },
    { key: "payRateStartDate", label: "เริ่มอัตราค่าจ้าง" },
    { key: "payRateEndDate", label: "สิ้นสุดอัตราค่าจ้าง" },
    { key: "paymentAmount", label: "จำนวนเงินช่วยเหลือ/อุดหนุน" },
    { key: "paymentDate", label: "วันที่จ่ายเงิน" },
  ],

  // Operation45 (มีรายละเอียดคอร์สย่อย)
  8: [
    { key: "supportNo", label: "เลขที่คำขอ" },
    { key: "supportName", label: "ผู้ขอรับเงินช่วยเหลือ/อุดหนุน" },
    { key: "innovationName", label: "นวัตกรรม" },
    { key: "innovationCourseName", label: "หลักสูตร" },
    { key: "updateDate", label: "ปรับปรุงล่าสุด" },
    { key: "classAmount", label: "จำนวนคลาส" },
    { key: "hour", label: "ชั่วโมงรวม" },
    { key: "personTotal", label: "จำนวนผู้เข้าอบรมรวม" },
    { key: "tutorHourAmount", label: "ค่าผู้สอนรวม" },
    { key: "materialAmount", label: "ค่าวัสดุรวม" },
    { key: "personAmount", label: "ค่าใช้จ่ายต่อคนรวม" },
    { key: "amount", label: "ค่าใช้จ่ายรวม" },
    { key: "startDate", label: "เริ่มดำเนินการ" },
    { key: "endDate", label: "สิ้นสุดดำเนินการ" },
    { key: "paymentAmount", label: "จำนวนเงินช่วยเหลือ/อุดหนุน" },
    { key: "paymentDate", label: "วันที่จ่ายเงิน" },
  ],

  // Operation46 (ถ้า supportTypeId == 3 หรือ 9 จะได้ชุดใดชุดหนึ่ง)
  3: [
    { key: "supportNo", label: "เลขที่คำขอ" },
    { key: "supportName", label: "ผู้ขอรับเงินช่วยเหลือ/อุดหนุน" },

    // ฝั่งทดสอบมาตรฐานฝีมือ (type 3)
    { key: "testLawOccupationName", label: "สาขาอาชีพ (ทดสอบ)" },
    { key: "testOccupationName", label: "สาขา (ทดสอบ)" },
    { key: "testPersonTotal", label: "จำนวนคน (ทดสอบ)" },
    { key: "testAmountTotal", label: "ยอดเงิน (ทดสอบ)" },
    { key: "testReceiptNo", label: "เลขที่ใบเสร็จ (ทดสอบ)" },
    { key: "testReceiptDate", label: "วันที่ใบเสร็จ (ทดสอบ)" },

    // ฝั่งประเมินความรู้ความสามารถ (type 9)
    { key: "estimateLawOccupationName", label: "สาขาอาชีพ (ประเมิน)" },
    { key: "estimateOccupationName", label: "สาขา (ประเมิน)" },
    { key: "estimatePersonTotal", label: "จำนวนคน (ประเมิน)" },
    { key: "estimateAmountTotal", label: "ยอดเงิน (ประเมิน)" },
    { key: "estimateReceiptNo", label: "เลขที่ใบเสร็จ (ประเมิน)" },
    { key: "estimateReceiptDate", label: "วันที่ใบเสร็จ (ประเมิน)" },

    { key: "paymentAmount", label: "จำนวนเงินช่วยเหลือ/อุดหนุน" },
    { key: "paymentDate", label: "วันที่จ่ายเงิน" },
  ],
  9: [
    { key: "supportNo", label: "เลขที่คำขอ" },
    { key: "supportName", label: "ผู้ขอรับเงินช่วยเหลือ/อุดหนุน" },

    // ฝั่งทดสอบมาตรฐานฝีมือ (type 3)
    { key: "testLawOccupationName", label: "สาขาอาชีพ (ทดสอบ)" },
    { key: "testOccupationName", label: "สาขา (ทดสอบ)" },
    { key: "testPersonTotal", label: "จำนวนคน (ทดสอบ)" },
    { key: "testAmountTotal", label: "ยอดเงิน (ทดสอบ)" },
    { key: "testReceiptNo", label: "เลขที่ใบเสร็จ (ทดสอบ)" },
    { key: "testReceiptDate", label: "วันที่ใบเสร็จ (ทดสอบ)" },

    // ฝั่งประเมินความรู้ความสามารถ (type 9)
    { key: "estimateLawOccupationName", label: "สาขาอาชีพ (ประเมิน)" },
    { key: "estimateOccupationName", label: "สาขา (ประเมิน)" },
    { key: "estimatePersonTotal", label: "จำนวนคน (ประเมิน)" },
    { key: "estimateAmountTotal", label: "ยอดเงิน (ประเมิน)" },
    { key: "estimateReceiptNo", label: "เลขที่ใบเสร็จ (ประเมิน)" },
    { key: "estimateReceiptDate", label: "วันที่ใบเสร็จ (ประเมิน)" },

    { key: "paymentAmount", label: "จำนวนเงินช่วยเหลือ/อุดหนุน" },
    { key: "paymentDate", label: "วันที่จ่ายเงิน" },
  ],
  10: [ //ประกาศ 6เดิม
    { key: "avgMan", label: "จำนวนลูกจ้างเฉลี่ย (คน)" },
    { key: "spkTrainMan", label: "มีการดำเนินการพัฒนาฝีมือแรงงานให้แก่ลูกจ้าง จำนวน (คน)" },
    { key: "perc", label: "คิดเป็นร้อยละ (ของลูกจ้างทั้งหมด)" },
    { key: "test", label: "ผ่านการทดสอบมาตรฐานฝีมือแรงงานแห่งชาติ จำนวน (คน)" },
    { key: "train", label: "จัดให้มีการฝึกอบรมฝีมือแรงงาน จำนวน (คน)" },
    { key: "oloc", label: "ผ่านการรับรองความรู้ความสามารถ จำนวน (คน)" },
    { key: "paymentAmount", label: "จำนวนเงินช่วยเหลือ/อุดหนุน" },
    { key: "paymentDate", label: "วันที่จ่ายเงิน" },
  ],
  11: [
    //ประะกาศ7 เดิม
    { key: "avgMan", label: "ลูกจ้างเฉลี่ย จำนวน (คน) " },
    { key: "avg70", label: "ร้อยละ 70 จำนวน (คน)" },
    { key: "spkTrainMan", label: "ดำเนินการพัฒนาฝีมือแรงงานให้แก่ลูกจ้าง (คน)" },
    { key: "percent", label: "คิดเป็นร้อยละ" },
    { key: "train", label: "มีการฝึกอบรมฝีมือแรงงาน จำนวน (คน)" },
    { key: "test", label: "ผ่านการทดสอบมาตรฐานฝีมือแรงงานแห่งชาติ จำนวน (คน)" },
    { key: "oloc", label: "ผ่านการรับรองความรู้ความสามารถ จำนวน (คน)" },
    { key: "paymentAmount", label: "จำนวนเงินช่วยเหลือ/อุดหนุน" },
    { key: "paymentDate", label: "วันที่จ่ายเงิน" },
  ],
};

// ฟิลด์ย่อยของ type 45 (ถ้ามี children: รายละเอียดคอร์ส)
const CHILD_TABLE_CONFIG_BY_TYPE = {
  45: [
    { key: "courseName", label: "หลักสูตร" },
    { key: "startDate", label: "เริ่ม" },
    { key: "endDate", label: "สิ้นสุด" },
    { key: "hour", label: "ชั่วโมง" },
    { key: "personTotal", label: "คน" },
    { key: "tutorHourAmount", label: "ค่าผู้สอน" },
    { key: "materialAmount", label: "ค่าวัสดุ" },
    { key: "amount", label: "รวม" },
  ],
};

function Row({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <tr>
      <th style={{ width: "36%" }}>{label}</th>
      <td>{String(value)}</td>
    </tr>
  );
}

export default function SupportDetailModal({
  isOpen,
  toggle,
  supportId,
  supportTypeId,
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");

  const fields = useMemo(() => {
    console.log("supportTypeId", supportTypeId);
    console.log("detail?.dataVersion", detail?.dataVersion);

    if (supportTypeId == 4 && detail?.dataVersion == null) {
      console.log("1")
      return FIELD_CONFIG[10];
    } else if (supportTypeId == 5 && detail?.dataVersion == null) {
      console.log("2")
      return FIELD_CONFIG[11];
    } else {
      return FIELD_CONFIG[supportTypeId];
    }
  }, [supportTypeId, detail]);

  const childTableConfig = useMemo(
    () => CHILD_TABLE_CONFIG_BY_TYPE[supportTypeId] || [],
    [supportTypeId, detail]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!supportId || !supportTypeId) return;
    let abort = false;

    const fetchDetail = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `/api/SupportOperation/detail/${Number(supportTypeId)}/${Number(supportId)}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!abort) setDetail(json);
      } catch (e) {
        if (!abort) setError("ไม่สามารถดึงข้อมูลได้");
      } finally {
        if (!abort) setLoading(false);
      }
    };

    fetchDetail();
    return () => {
      abort = true;
    };
  }, [isOpen, supportId, supportTypeId]);

  const data = detail?.data ?? {};
  // console.log("data", data)
  const children = detail?.children ?? []; // สำหรับกรณี type 45



  console.log("FIELD_CONFIG", FIELD_CONFIG);
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
      <ModalHeader toggle={toggle}>
        <span className="me-2">รายละเอียด</span>
        {/* <Badge pill color="primary">
          ประเภท {supportTypeId}
        </Badge> */}
      </ModalHeader>

      <ModalBody>
        {loading && (
          <div className="d-flex align-items-center gap-2">
            <Spinner size="sm" />
            <span>กำลังโหลดข้อมูล...</span>
          </div>
        )}

        {!loading && error && (
          <div className="text-danger">{error}</div>
        )}

        {!loading && !error && (
          <>
            {/* ตารางหลัก */}
            <Table bordered responsive size="sm">
              <tbody>
                {fields?.map((f) => {
                  console.log("f", f)
                  return (
                    <Row key={f.key} label={f.label} value={data?.[f.key]} />
                  )
                })}
              </tbody>
            </Table>

            {/* ตารางย่อย (ถ้ามี) เช่น type 45 */}
            {Array.isArray(children) && children.length > 0 && childTableConfig.length > 0 && (
              <>
                <h6 className="mt-3 mb-2">รายละเอียดหลักสูตร</h6>
                <Table bordered responsive size="sm">
                  <thead>
                    <tr>
                      {childTableConfig.map((c) => (
                        <th key={c.key}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {children.map((row, idx) => (
                      <tr key={idx}>
                        {childTableConfig.map((c) => (
                          <td key={c.key}>{row?.[c.key] ?? "-"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>ปิด</Button>
      </ModalFooter>
    </Modal>
  );
}
